// @file All network requests used by dashboard store
import {
  Billing as BillingType,
  fetchBilling,
  fetchInvoices,
  Invoice as InvoiceType,
  JsonAPIResponse,
  JsonAPIUpdateRequest,
  User as UserType,
  Wall as WallType,
} from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'
import { ApolloError, FetchPolicy, MutationOptions, QueryOptions } from 'apollo-client'
import { apolloClient } from 'bits/apollo'
import { currentHostWithProtocol } from 'bits/location'
import {
  ADD_TO_FOLDER,
  CREATE_FOLDER,
  DELETE_FOLDER,
  MOVE_TO_FOLDER,
  REMOVE_FROM_FOLDER,
  UPDATE_FOLDER,
  USER_FOLDERS,
  WALL_FOLDERS,
} from 'graphql/folders'
import {
  ARCHIVE_PADLET,
  DELETE_PADLET,
  MUTE_PUSH_NOTIFICATIONS,
  UNARCHIVE_PADLET,
  UNMUTE_PUSH_NOTIFICATIONS,
  USER_PADLETS,
  WALL_NOTIFICATIONS,
} from 'graphql/padlets'
import template from 'lodash.template'
import { fetchJson } from 'surface/api_fetch'
import {
  AdvertisedQuotasApiResult,
  BillingPauseResult,
  DomainValidation,
  DomainValidationParams,
  EstimateSwitchingCostResult,
  LibraryPlan as LibraryPlanType,
  Plan as PlanType,
  ScheduledChangesResult,
  SwitchCurrencyResult,
  SwitchPlanResult,
  Tenant as TenantType,
  TenantInviteLinks as TenantInviteLinksType,
  TenantInviteLinksParams,
  TenantParams,
  UndoScheduledChangeResult,
} from 'types'

const DEFAULT_LOAD_COUNT = 20
const API_VERSION = 5

type UserPadletsQueryVariable = {
  userId?: number
  folderId?: number
  wallsFilter?: string
  afterCursor?: string
  loadCount?: number
  shouldFetchLinks?: boolean
}

type QueryResponse<T> = {
  data: T
  loading: boolean
  networkStatus?: number
  stale?: boolean
}

type WallNotificationSetting = {
  push_notify: boolean
  wall_id: number
}

const fetchWallsQueryParams = ({
  userId,
  folderId,
  wallsFilter,
  afterCursor,
  loadCount = DEFAULT_LOAD_COUNT,
  shouldFetchLinks = false,
}: UserPadletsQueryVariable): QueryOptions<UserPadletsQueryVariable> => {
  const query = USER_PADLETS
  const variables: UserPadletsQueryVariable = { userId, afterCursor, loadCount, shouldFetchLinks }
  const fetchPolicy: FetchPolicy = 'network-only'
  if (folderId) {
    variables.folderId = folderId
  } else {
    variables.wallsFilter = wallsFilter
  }
  return { query, variables, fetchPolicy }
}

async function fetchWallsQuery(variables: UserPadletsQueryVariable): Promise<any> {
  const params = fetchWallsQueryParams(variables)
  return apolloClient.query(params)
}

const fetchFoldersQueryParams = (userId: number): QueryOptions => {
  const query = USER_FOLDERS
  const variables = { userId }
  const fetchPolicy: FetchPolicy = 'network-only'
  return { query, variables, fetchPolicy }
}

async function fetchFoldersQuery(variables): Promise<any> {
  const params = fetchFoldersQueryParams(variables)
  return apolloClient.query(params)
}

const fetchWallFoldersQueryParams = (wallId: number): QueryOptions => {
  const query = WALL_FOLDERS
  const variables = { wallId }
  return { query, variables }
}

async function fetchWallFoldersQuery(variables): Promise<any> {
  const params = fetchWallFoldersQueryParams(variables)
  return apolloClient.query(params)
}

const deleteWallMutationParams = (wallId: number): MutationOptions => {
  const mutation = DELETE_PADLET
  const variables = { wallId }
  return { mutation, variables }
}

async function deleteWallMutation(wallId: number): Promise<any> {
  const params = deleteWallMutationParams(wallId)
  return apolloClient.mutate(params)
}

const archiveWallMutationParams = (wallId: number): MutationOptions => {
  const mutation = ARCHIVE_PADLET
  const variables = { wallId }
  return { mutation, variables }
}

async function archiveWallMutation(wallId: number): Promise<any> {
  const params = archiveWallMutationParams(wallId)
  return apolloClient.mutate(params)
}

const unarchiveWallMutationParams = (wallId: number): MutationOptions => {
  const mutation = UNARCHIVE_PADLET
  const variables = { wallId }
  return { mutation, variables }
}

async function unarchiveWallMutation(wallId: number): Promise<any> {
  const params = unarchiveWallMutationParams(wallId)
  return apolloClient.mutate(params)
}

const createFolderMutationParams = (name: string): MutationOptions => {
  const mutation = CREATE_FOLDER
  const variables = { name }
  return { mutation, variables }
}

async function createFolderMutation(name: string): Promise<any> {
  const params = createFolderMutationParams(name)
  return apolloClient.mutate(params)
}

const updateFolderMutationParams = ({ name, folderId }): MutationOptions => {
  const mutation = UPDATE_FOLDER
  const variables = { name, folderId }
  return { mutation, variables }
}

async function updateFolderMutation({ name, folderId }): Promise<any> {
  const params = updateFolderMutationParams({ name, folderId })
  return apolloClient.mutate(params)
}

const deleteFolderMutationParams = (folderId: number): MutationOptions => {
  const mutation = DELETE_FOLDER
  const variables = { folderId }
  return { mutation, variables }
}

async function deleteFolderMutation(folderId: number): Promise<any> {
  const params = deleteFolderMutationParams(folderId)
  return apolloClient.mutate(params)
}

const addToFolderMutationParams = ({ userId, folderId, wallId }): MutationOptions => {
  const mutation = ADD_TO_FOLDER
  const variables = { folderId, wallId }
  const refetchQueries = [fetchWallsQueryParams({ userId, folderId }), fetchWallFoldersQueryParams(wallId)]
  return { mutation, variables, refetchQueries }
}

async function addToFolderMutation({ userId, folderId, wallId }): Promise<any> {
  const params = addToFolderMutationParams({ userId, folderId, wallId })
  return apolloClient.mutate(params)
}

const removeFromFolderMutationParams = ({ folderId, wallId, userId }): MutationOptions => {
  const mutation = REMOVE_FROM_FOLDER
  const variables = { folderId, wallId }
  const refetchQueries = [fetchWallsQueryParams({ userId, folderId }), fetchWallFoldersQueryParams(wallId)]
  return { mutation, variables, refetchQueries }
}

async function removeFromFolderMutation({ folderId, wallId, userId }): Promise<any> {
  const params = removeFromFolderMutationParams({ folderId, wallId, userId })
  return apolloClient.mutate(params)
}

const moveToFolderMutationParams = ({ userId, toFolderId, fromFolderId, wallId }): MutationOptions => {
  const mutation = MOVE_TO_FOLDER
  const variables = { toFolderId, fromFolderId, wallId }
  const refetchQueries = [
    fetchWallsQueryParams({ userId, folderId: toFolderId }),
    fetchWallsQueryParams({ userId, folderId: fromFolderId }),
    fetchWallFoldersQueryParams(wallId),
  ]
  return { mutation, variables, refetchQueries }
}

async function moveToFolderMutation({ userId, toFolderId, fromFolderId, wallId }): Promise<any> {
  const params = moveToFolderMutationParams({ userId, toFolderId, fromFolderId, wallId })
  return apolloClient.mutate(params)
}

function fetchFirstGraphQLError(e: unknown): null | string {
  if (!e) return null
  return (e as ApolloError)?.graphQLErrors?.[0]?.message
}

class Billing {
  public static fetch(): Promise<JsonAPIResponse<BillingType>> {
    return fetchBilling()
  }
  public static async pause(fetchOptions = {}): Promise<BillingPauseResult> {
    //note: not a JSON:API formatted api call
    return await fetchJson('/premium/pause', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async resume(fetchOptions = {}): Promise<BillingPauseResult> {
    //note: not a JSON:API formatted api call
    return await fetchJson('/premium/resume', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async cancelPause(fetchOptions = {}): Promise<BillingPauseResult> {
    //note: not a JSON:API formatted api call
    return await fetchJson('/premium/cancel_pausing', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async undoScheduledCancellation(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_cancellation', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async undoScheduledSwitch(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_plan_switch', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateSwitchingCost(fetchOptions = {}): Promise<EstimateSwitchingCostResult> {
    return await fetchJson('/premium/switch_plan/estimate_cost', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchPlan(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/switch_plan', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchCurrency(fetchOptions = {}): Promise<SwitchCurrencyResult> {
    return await fetchJson('/premium/switch_currency', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async cancelMembership(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/cancel', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async fetchScheduledChanges(): Promise<ScheduledChangesResult> {
    return await fetchJson('/api/3/billing/scheduled_changes', {
      method: HTTPMethod.get,
    })
  }
}

class Invoice {
  public static fetch(): Promise<JsonAPIResponse<InvoiceType>> {
    return fetchInvoices()
  }
}

class Plan {
  /**
   * Fetch list of available plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<PlanType>> {
    return await fetchJson(`/api/${API_VERSION}/plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchAdvertisedQuotas(fetchOptions = {}): Promise<AdvertisedQuotasApiResult> {
    return await fetchJson('/api/3/membership_tier_quotas', {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class LibraryPlan {
  /**
   * Fetch list of available library plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<LibraryPlanType>> {
    return await fetchJson(`/api/${API_VERSION}/team_plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Library {
  public static get libraryUrl(): string {
    return `/api/1/libraries?userId=\${ userId }&filter=\${ filter }`
  }

  public static buildUrl(url, options): string {
    return template(url)({ ...options, interpolate: /\${([\s\S]+?)}/g })
  }

  public static async fetchCreatableLibraries({ userId }, fetchOptions = {}): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId: userId, filter: 'wall_creatable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class User {
  public static async changePassword(id, fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/users/${id}/change_password`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async updateBasicInfo(id, fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/users/${id}`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async fetchUserWalls(id, fetchOptions = {}): Promise<Array<WallType>> {
    return await fetchJson(`/users/${id}/walls`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchWallNotificationSettings(
    userId: number,
  ): Promise<QueryResponse<{ user: { wall_notifications: Array<WallNotificationSetting> } }>> {
    const query = WALL_NOTIFICATIONS
    const variables = { userId }
    return apolloClient.query({
      query,
      variables,
    })
  }

  public static async fetchCommunicationSettings(
    id,
    fetchOptions = {},
  ): Promise<{ communication: { wall_create: boolean; product_update: boolean } }> {
    return await fetchJson(`/users/${id}/settings`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateCommunicationSettings(
    id,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<{ communication: { wall_create: boolean; product_update: boolean } }>> {
    return await fetchJson(`/users/${id}/settings`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async toggleAllNotifications(fetchOptions = {}): Promise<{ notify_all: boolean }> {
    return await fetchJson(`${currentHostWithProtocol()}/_/notifications`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async mutePushNotifications(wallId: number): Promise<any> {
    const mutation = MUTE_PUSH_NOTIFICATIONS
    const variables = { wallId }
    return apolloClient.mutate({
      mutation,
      variables,
    })
  }

  public static async unmutePushNotifications(wallId: number): Promise<any> {
    const mutation = UNMUTE_PUSH_NOTIFICATIONS
    const variables = { wallId }
    return apolloClient.mutate({
      mutation,
      variables,
    })
  }
}

class Tenant {
  public static async updateTenantInfo(id, fetchOptions: Partial<TenantParams>): Promise<JsonAPIResponse<TenantType>> {
    const body: string = JSON.stringify(fetchOptions)
    return fetchJson(`/tenants/${id}`, {
      method: HTTPMethod.put,
      body,
    })
  }

  public static async isSubdomainValid(params: DomainValidationParams): Promise<DomainValidation> {
    return fetchJson(`/tenants/subdomain_valid?` + new URLSearchParams(params), {
      method: HTTPMethod.get,
    })
  }
}

class TenantInviteLinks {
  public static async get(id: number): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    return fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.get,
    })
  }

  public static async patch(
    id: number,
    fetchOptions: JsonAPIUpdateRequest<Partial<TenantInviteLinksParams>>,
  ): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    const body: string = JSON.stringify(fetchOptions)
    return fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.patch,
      body,
    })
  }
}

class TenantPermissions {
  public static async fetch(): Promise<JsonAPIResponse<Tenant>> {
    return fetchJson(`/api/tenant_permissions`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(tenant: Partial<TenantType>): Promise<JsonAPIResponse<Tenant>> {
    return fetchJson(`/api/tenant_permissions`, {
      method: HTTPMethod.patch,
      body: JSON.stringify({ data: { type: 'tenant', id: tenant.id, attributes: tenant } }),
    })
  }
}
class Lti {
  public static async generateLtiDynamicRegistrationUrl(id: number): Promise<{ url: string; tenantId: number }> {
    return fetchJson(`/api/lti/dynamic_registration_url?id=${id}`, {
      method: HTTPMethod.get,
    })
  }

  public static async generateLtiLinkDeploymentToken(id: number): Promise<{ token: string; tenantId: number }> {
    return fetchJson(`/api/lti/link_deployment_token?id=${id}`, {
      method: HTTPMethod.get,
    })
  }
}

export {
  fetchWallsQuery,
  fetchFoldersQuery,
  fetchWallFoldersQuery,
  deleteWallMutation,
  archiveWallMutation,
  unarchiveWallMutation,
  createFolderMutation,
  updateFolderMutation,
  deleteFolderMutation,
  addToFolderMutation,
  removeFromFolderMutation,
  moveToFolderMutation,
  fetchFirstGraphQLError,
  Billing,
  Invoice,
  Library,
  LibraryPlan,
  Plan,
  User,
  Tenant,
  TenantInviteLinks,
  TenantPermissions,
  Lti,
}
